import { css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { useScrollPosition } from '@hooks/useScrollPosition';
import { FontSizes, Radius, Spacing } from 'chatie-design-system';

import { DecoratedButton } from '@components/common/Button';
import Empty from '@components/common/Empty';
import { PaginationIterator } from '@components/common/Pagination';
import Pad from '@components/layout/Pad';

import {
  deleteMyCommunityPost,
  fetchUserPosts,
  togglePostLike,
} from '@api/community';
import {
  MarketItemType,
  Platform,
  QUERY_KEYS,
  ReportObjectType,
} from '@constants';
import { useGlobalContext } from '@contexts/global';
import { ICommunityPost } from '@model';
import { useUserMarketItemInventory } from '@utils/hook';
import { toQueryString } from '@utils/misc';
import { useOptimisticUpdate, usePaginationQuery } from '@utils/query';
import { useWebView } from '@utils/webview';

import Post, { PostCardAction } from './Post';

const writeButtonCSS = css`
  padding: ${Spacing.medium}px;
  button {
    box-shadow: none;
    font-weight: 400;
  }
`;
const userButtonCSS = css`
  width: 100%;
  font-size: ${FontSizes.small}px;
  font-weight: 600;
  flex: 1 1 0px;
  height: 48px;
  border-radius: ${Radius.medium}px;
`;
interface PostListProps {
  userId: number;
  isMyPage?: boolean;
}

const PostList = ({ userId, isMyPage }: PostListProps) => {
  const { t } = useTranslation();
  const { web } = useWebView();
  const { platform } = useGlobalContext();
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data, getDecoration } = useUserMarketItemInventory(userId);
  const profileBtnDecoration = getDecoration(data, MarketItemType.profileBtn);

  const queryKey = QUERY_KEYS.POST.USER_LIST(userId);
  const query = usePaginationQuery(
    queryKey,
    ({ pageParam }) => fetchUserPosts(userId, pageParam),
    {
      refetchOnWindowFocus: 'always',
    }
  );

  const optimistic = useOptimisticUpdate();
  const { mutate: toggleLike } = useMutation(togglePostLike);

  const { mutate: removePost } = useMutation((postId: number) =>
    deleteMyCommunityPost(postId)
  );

  useScrollPosition(`userPostList:${userId}`);

  const likePost = useCallback((post: ICommunityPost) => {
    toggleLike(post.id, {
      onSuccess: ({ isLiked }) => {
        optimistic.update<ICommunityPost>(
          queryKey,
          (post) => ({
            ...post,
            isLiked,
            likeCount: post.likeCount + (isLiked ? 1 : -1),
          }),
          { id: post.id }
        );
      },
    });
  }, []);

  const pollVote = useCallback((post: ICommunityPost) => {
    optimistic.update<ICommunityPost>(
      queryKey,
      (p) => ({ ...p, poll: post.poll }),
      { id: post.id }
    );
    const feedQueries = queryClient.getQueriesData<unknown>(['communityFeed']);
    feedQueries.forEach(([key]) => {
      optimistic.update<ICommunityPost>(
        key,
        (p) => ({ ...p, poll: post.poll }),
        { id: post.id }
      );
    });
    queryClient.invalidateQueries(['communityFeed']);
    queryClient.invalidateQueries(QUERY_KEYS.FOLLOWING.FEED);
    queryClient.invalidateQueries(QUERY_KEYS.POST.ONE(post.id));
  }, []);

  const handleNavigatePostCreate = () => {
    if (platform === Platform.web) {
      router.push('/feeds/posts/create');
    }
    web.openPage('/feeds/posts/create');
  };

  const handleNavigatePostDetail = (id: number) => {
    if (platform === Platform.web) {
      router.push(`/feeds/posts/${id}`);
    }
    web.openPage(`/feeds/posts/${id}`);
  };

  const handleDeletePost = (id: number) => {
    if (confirm(t('정말 게시글을 삭제하시겠습니까?'))) {
      removePost(id, {
        onSuccess: () => queryClient.invalidateQueries(queryKey),
      });
    }
  };

  const handleActionSheet = (value: PostCardAction, id: number) => {
    switch (value) {
      case 'modify':
        if (platform === Platform.web) {
          router.push(
            `/feeds/posts/create/${toQueryString({ postId: id })}`
          );
        }
        web.openPage(
          `/feeds/posts/create/${toQueryString({ postId: id })}`
        );
        break;
      case 'delete':
        handleDeletePost(id);
        break;
      case 'report':
        router.push(
          `/report${toQueryString({
            objectType: ReportObjectType.community,
            objectId: id,
          })}`
        );
        break;
      default:
        return;
    }
  };

  return (
    <>
      {isMyPage && (
        <div css={writeButtonCSS}>
          <DecoratedButton
            css={userButtonCSS}
            decoration={profileBtnDecoration}
            onClick={handleNavigatePostCreate}
          >
            새로운 게시글 쓰기
          </DecoratedButton>
        </div>
      )}
      <PaginationIterator name="user-post-list" query={query} border>
        {(item) => (
          <Pad onClick={() => handleNavigatePostDetail(item.id)}>
            <Post
              {...item}
              clip
              onLike={likePost}
              onPollVote={pollVote}
              isMyPage={isMyPage}
              handleActionSheet={handleActionSheet}
            />
          </Pad>
        )}
      </PaginationIterator>
      <Empty query={query}>
        <p>{t('작성한 게시글이 없습니다.')}</p>
      </Empty>
    </>
  );
};

export default PostList;
